import { useCallback, useEffect, useMemo } from "react";
import styles from "./Events.module.css";
import { Loader } from "../../../components/Loader/Loader";
import { Link as StyledLink } from "../../../components/Link/Link";
import { Meta } from "../../../components/Meta/Meta";
import { H2 } from "../../../components/Heading/Heading";
import { Pane, Paragraph } from "evergreen-ui";
import { useReq } from "@larner.dev/use-req";
import { Spark, SparkWithRegistrationSummary } from "@greenflagdate/shared";
import { useGFTheme } from "../../../lib/themes";
import { apiReq } from "../../../lib/apiReq";
import { useStoreData } from "../../../lib/store/store";
import { isLoggedIn } from "../../../lib/isLoggedIn";

import { SparkCard } from "../../../components/SparkCard/SparkCard";
import dayjs from "dayjs";
const ageInRange = (age: number, spark: Spark) => {
  if (age < spark.min_age) {
    return false;
  }
  if (spark.max_age && age > spark.max_age) {
    return false;
  }
  return true;
};
export const Events = () => {
  const [sparksReq, sparksReqState] =
    useReq<SparkWithRegistrationSummary[]>(apiReq);
  const [mySparksReq, mySparksReqState] =
    useReq<SparkWithRegistrationSummary[]>(apiReq);
  const store = useStoreData();
  const loggedIn = useMemo(() => isLoggedIn(store), [store]);
  const { colors } = useGFTheme();
  const refreshMySparks = useCallback(() => {
    if (loggedIn) {
      mySparksReq.get("/user/sparks");
    }
  }, [loggedIn, mySparksReq]);
  useEffect(() => {
    sparksReq.get(`/spark/current`);
  }, [sparksReq]);
  useEffect(() => {
    refreshMySparks();
  }, [refreshMySparks]);
  const unregisteredEvents = useMemo(() => {
    const unregistered =
      sparksReqState.result?.filter(
        (s1) =>
          !loggedIn || !mySparksReqState.result?.some((s2) => s1.id === s2.id)
      ) || [];
    unregistered.sort((a, b) => {
      const aEnds = new Date(a.ends_at).getTime();
      const bEnds = new Date(b.ends_at).getTime();
      if (!loggedIn) {
        return aEnds - bEnds;
      } else {
        const age = dayjs().diff(store.user?.birth_date, "years");
        const aInRange = ageInRange(age, a);
        const bInRange = ageInRange(age, b);
        if (aInRange && !bInRange) {
          return -1;
        } else if (bInRange && !aInRange) {
          return 1;
        }
        return aEnds - bEnds;
      }
    });
    return unregistered;
  }, [
    loggedIn,
    mySparksReqState.result,
    sparksReqState.result,
    store.user?.birth_date,
  ]);

  if (!sparksReqState.result || sparksReqState.loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader />
      </div>
    );
  }

  if (!sparksReqState.loading) {
    if (sparksReqState.error) {
      return <div>{sparksReqState.error.toString()}</div>;
    }
  }

  return (
    <>
      <Meta>
        <title>Green Flag Events</title>
        <meta property="og:title" content="Green Flag Events" />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.greenflagdate.com/events"
        />
      </Meta>
      <Pane
        maxWidth="40rem"
        marginX="auto"
        marginTop="1.5rem"
        paddingX="1.5rem"
      >
        <H2 marginBottom="1.5rem">
          {loggedIn && store.user?.first_name
            ? `Welcome ${store.user.first_name}`
            : "Green Flag Events"}
          !
        </H2>
        {!loggedIn && (
          <Pane marginBottom="2rem">
            All Events are currently in Atlanta. If you are interested in
            hosting an event in your city please{" "}
            <StyledLink to="/contact">let us know!</StyledLink>
          </Pane>
        )}
        {loggedIn && !!mySparksReqState.result?.length && (
          <>
            <Paragraph size={400} color={colors.greenDark}>
              MY RESERVATIONS
            </Paragraph>
            {mySparksReqState.result.map((spark) => (
              <SparkCard
                key={spark.id}
                spark={spark}
                isRegistered={true}
                onCancelReservation={refreshMySparks}
                onRegister={refreshMySparks}
              />
            ))}
          </>
        )}
        {unregisteredEvents.length ? (
          <>
            <Paragraph size={400} color={colors.greenDark}>
              OTHER UPCOMING EVENTS
            </Paragraph>
            {unregisteredEvents.map((spark) => (
              <SparkCard
                key={spark.id}
                spark={spark}
                isRegistered={false}
                onCancelReservation={refreshMySparks}
                onRegister={refreshMySparks}
              />
            ))}
          </>
        ) : (
          <Pane marginBottom="2rem">
            We don't have any events scheduled at this time. If you are
            interested in hosting an event in your city please{" "}
            <StyledLink to="/contact">let us know!</StyledLink>
          </Pane>
        )}
      </Pane>
    </>
  );
};
